import React from 'react';
import { Switch, Route, useLocation } from 'wouter';
import { useEffect, useState } from 'react';
import { queryClient } from './lib/queryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider } from '@/hooks/use-auth';
import { SidebarProvider, useSidebar } from '@/hooks/use-sidebar';
import { CallProvider } from '@/contexts/CallContext';
import { FloatingCallPanel } from '@/components/calling/FloatingCallPanel';
import { IncomingCallDialog } from '@/components/calling/IncomingCallDialog';
import { ProtectedRoute } from './lib/protected-route';
import AuthRedirectHandler from '@/components/auth-redirect-handler';
import MainHeader from '@/components/main-header';
import NavSidebar from '@/components/nav-sidebar';
import { initializeConsoleFilter } from '@/lib/console-filter';

// Core feature imports - Authentication, Users, Dashboard
import { HomePage, AuthPage, NotFoundPage } from '@/features/core/pages';

// Sales feature imports - Customers, Proposals, Pipeline
import {
  ProposalsPage,
  CustomerMasterPage,
  ProposalDetailPage,
} from '@/features/sales/pages';

// Import pipeline with user's Pipeline Progress and minimization features
import PipelinePage from '@/pages/pipeline';

// Import analytics dashboard
import AnalyticsPage from '@/pages/analytics';

// Import the main customers page with modal functionality
import CustomersPage from '@/pages/customers';

// Operations feature imports - Installations, Surveys, Scheduling
import {
  InstallationsPage,
  InstallationDetailPage,
  NewInstallationPage,
  EditInstallationPage,
} from '@/features/operations/pages';

// Communication feature imports - Email campaigns, templates, diagnostics
import {
  EmailTemplatesPage,
  EditEmailTemplatePage,
  SendTestEmailPage,
  EmailDiagnosticsPage,
  TestLoaEmailPage,
} from '@/features/communication/pages';

// Finance feature imports - Invoicing, payments, LOAs
import {
  LoaFormPage,
  LoaDetailPage,
  LoaListPage,
  LoaPublicSignPage,
  CompletionSignPage,
} from '@/features/finance/pages';

// AI Enhancement pages
import AIAgentPage from '@/pages/AIAgentPage';
import Phase3AgentPage from '@/pages/Phase3AgentPage';
import Mem0TestPage from '@/pages/Mem0TestPage';
import RAGTestPage from '@/pages/RAGTestPage';

// Site Survey pages
import SiteSurveyPage from '@/pages/SiteSurveyPage';
import SurveyManagementPage from '@/pages/SurveyManagementPage';

// HIES Completion pages
import HiesCompletionPage from '@/pages/HiesCompletionPage';

// Scheduling pages
import SchedulingDashboard from '@/features/scheduling/pages/SchedulingDashboard';
import SchedulingCalendar from '@/features/scheduling/pages/SchedulingCalendar';
import StaffManagement from '@/features/scheduling/pages/StaffManagement';

// Legacy pages - to be migrated to features in future iterations
import ExternalSyncPage from '@/pages/external-sync';
import CalendarTest from '@/pages/calendar-test';
import CalendarBookingPage from '@/pages/calendar-booking';
import PDFTestPage from '@/pages/pdf-test';
import DocumentTestPage from '@/pages/document-test';
import DocumentViewerPage from '@/pages/document-viewer-page';
import OpenSolarPage from '@/pages/opensolar/new-index';
import OpenSolarProjectsPage from '@/pages/opensolar/projects';
import OpenSolarProjectDetailPage from '@/pages/opensolar/projects/[id]';
import OpenSolarDesignViewPage from '@/pages/opensolar/design-view';
import OpenSolarDesignerPage from '@/pages/opensolar/designer';
import OpenSolarSettingsPage from '@/pages/opensolar/settings';
import MetaConversionsSettings from '@/pages/meta-conversions-settings';

// Support Tickets pages
import SupportTicketsPage from '@/pages/support-tickets';
import SupportTicketDetailPage from '@/pages/support-ticket-detail';
import SupportTicketCreatePage from '@/pages/support-ticket-create';

import { PWAInstallBanner } from '@/components/ui/pwa-install-banner';
import { ServiceWorkerUpdatePrompt } from '@/components/ServiceWorkerUpdatePrompt';
import { useServiceWorker } from '@/hooks/use-service-worker';
import { versionManager } from '@/lib/version-manager';

function Router() {
  return (
    <Switch>
      <ProtectedRoute path="/" component={HomePage} />
      <ProtectedRoute path="/customers" component={CustomersPage} />
      <ProtectedRoute path="/customers/new" component={CustomersPage} />
      <ProtectedRoute path="/customers/:id" component={CustomerMasterPage} />
      <ProtectedRoute path="/leads" component={() => { window.location.href = '/pipeline'; return null; }} />
      <ProtectedRoute path="/proposals" component={ProposalsPage} />
      <ProtectedRoute path="/proposals/:id" component={ProposalDetailPage} />
      <ProtectedRoute path="/loa-form/:id" component={LoaFormPage} />
      <ProtectedRoute path="/loa/new" component={LoaFormPage} />
      <ProtectedRoute path="/loa/:id" component={LoaDetailPage} />
      <ProtectedRoute path="/loa" component={LoaListPage} />
      <ProtectedRoute path="/installations" component={InstallationsPage} />
      <ProtectedRoute path="/installations/new" component={NewInstallationPage} />
      <ProtectedRoute path="/installations/new/:proposalId" component={NewInstallationPage} />
      <ProtectedRoute
        path="/installations/new/:proposalId/:customerId"
        component={NewInstallationPage}
      />
      <ProtectedRoute
        path="/installations/:id/edit"
        component={EditInstallationPage as () => React.JSX.Element}
      />
      <ProtectedRoute
        path="/installations/:id"
        component={InstallationDetailPage as () => React.JSX.Element}
      />
      <ProtectedRoute path="/site-surveys" component={SiteSurveyPage} />
      <ProtectedRoute path="/survey-management" component={SurveyManagementPage} />
      <ProtectedRoute path="/hies-completion" component={HiesCompletionPage} />
      <ProtectedRoute path="/scheduling" component={SchedulingDashboard} />
      <ProtectedRoute path="/scheduling/calendar" component={SchedulingCalendar} />
      <ProtectedRoute path="/scheduling/staff" component={StaffManagement} />
      <ProtectedRoute path="/support-tickets" component={SupportTicketsPage} />
      <ProtectedRoute path="/support-tickets/new" component={SupportTicketCreatePage} />
      <ProtectedRoute path="/support-tickets/:id" component={SupportTicketDetailPage} />
      <ProtectedRoute path="/test-email" component={SendTestEmailPage} />
      <ProtectedRoute path="/email-diagnostics" component={EmailDiagnosticsPage} />
      <ProtectedRoute path="/test-loa-email" component={TestLoaEmailPage} />
      <ProtectedRoute path="/external-sync" component={ExternalSyncPage} />
      <ProtectedRoute path="/pipeline" component={PipelinePage} />
      <ProtectedRoute path="/analytics" component={AnalyticsPage} />
      <ProtectedRoute path="/opensolar" component={OpenSolarPage} />
      <ProtectedRoute path="/opensolar/projects" component={OpenSolarProjectsPage} />
      <ProtectedRoute path="/opensolar/projects/:id" component={OpenSolarProjectDetailPage} />
      <ProtectedRoute path="/opensolar/design-view" component={OpenSolarDesignViewPage} />
      <ProtectedRoute path="/opensolar/designer" component={OpenSolarDesignerPage} />
      <ProtectedRoute path="/opensolar/designer/:id" component={OpenSolarDesignerPage} />
      <ProtectedRoute path="/opensolar/settings" component={OpenSolarSettingsPage} />
      <ProtectedRoute path="/ai-agent" component={AIAgentPage} />
      <ProtectedRoute path="/mem0-test" component={Mem0TestPage} />
      <ProtectedRoute path="/rag-test" component={RAGTestPage} />
      <ProtectedRoute path="/settings/meta-conversions" component={MetaConversionsSettings} />
      <ProtectedRoute
        path="/email-templates"
        component={EmailTemplatesPage as () => React.JSX.Element}
      />
      <ProtectedRoute
        path="/email-templates/:id/edit"
        component={EditEmailTemplatePage as () => React.JSX.Element}
      />
      <ProtectedRoute
        path="/email-templates/new"
        component={EditEmailTemplatePage as () => React.JSX.Element}
      />
      <ProtectedRoute path="/calendar-test" component={CalendarTest} />
      <ProtectedRoute path="/calendar-booking" component={CalendarBookingPage} />
      <ProtectedRoute path="/pdf-test" component={PDFTestPage} />
      <ProtectedRoute path="/document-test" component={DocumentTestPage} />
      <Route path="/document/:id" component={DocumentViewerPage} />
      <Route path="/auth" component={AuthPage} />
      <Route path="/reset-password/:token" component={AuthPage} />
      <Route path="/auth-redirect" component={AuthRedirectHandler} />
      <Route path="/loa-sign/:token" component={LoaPublicSignPage} />
      <Route path="/completion-sign/:token" component={CompletionSignPage} />
      <Route component={NotFoundPage} />
    </Switch>
  );
}

function App() {
  const { registration } = useServiceWorker();

  // Initialize console filter to suppress benign warnings in development
  useEffect(() => {
    initializeConsoleFilter();
  }, []);

  // Initialize version manager for app-level version tracking
  useEffect(() => {
    console.log('[App] Initializing version manager...');
    versionManager.initialize().catch((error) => {
      console.error('[App] Failed to initialize version manager:', error);
    });

    return () => {
      versionManager.destroy();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CallProvider>
          <TooltipProvider delayDuration={150}>
            <AppContent />
            {/* Floating call panel - shows during active calls */}
            <FloatingCallPanel />
            {/* Incoming call dialog - shows accept/reject UI for incoming calls */}
            <IncomingCallDialog />
            {/* Service Worker update prompt - handles all version updates */}
            <ServiceWorkerUpdatePrompt registration={registration} />
            <Toaster />
          </TooltipProvider>
        </CallProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function AppContent() {
  const [location] = useLocation();
  const isAuthPage = location === '/auth';
  const isResetPasswordPage = location.startsWith('/reset-password/');
  const isAuthRedirect = location === '/auth-redirect' || location.startsWith('/auth-redirect?');
  const isPublicPage =
    location.startsWith('/loa-sign/') ||
    location.startsWith('/completion-sign/') ||
    location.startsWith('/document/');

  // Initialize theme based on user preference on first load
  useEffect(() => {
    const root = window.document.documentElement;
    const savedTheme = localStorage.getItem('theme') || 'system';

    if (savedTheme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.classList.toggle('dark', prefersDark);
    } else {
      root.classList.toggle('dark', savedTheme === 'dark');
    }
  }, []);

  // Initialize console filter
  useEffect(() => {
    initializeConsoleFilter();
  }, []);

  // Don't show header/sidebar on auth pages, password reset pages, auth redirect pages, or public LOA signing pages
  const hideHeaderSidebar = isAuthPage || isResetPasswordPage || isAuthRedirect || isPublicPage;

  if (hideHeaderSidebar) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <Router />
      </div>
    );
  }

  return (
    <SidebarProvider>
      <AppLayoutInner />
    </SidebarProvider>
  );
}

function AppLayoutInner() {
  const { collapsed } = useSidebar();

  return (
    <div className="min-h-screen flex w-full bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100">
      <NavSidebar />
      <div className="flex flex-1 flex-col min-w-0">
        <MainHeader />
        <main className="flex-1 overflow-auto p-4">
          <Router />
        </main>
      </div>
    </div>
  );
}

// Export App wrapped with Sentry Error Boundary
import { SentryErrorBoundary } from './lib/sentry';

function ErrorFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-900">
      <div className="text-center p-8 max-w-md">
        <h1 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">
          Oops! Something went wrong
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          We've encountered an unexpected error. The error has been reported and we'll look into it.
        </p>
        <details className="text-left mb-4">
          <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            Error details
          </summary>
          <pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-auto">
            {errorMessage}
          </pre>
        </details>
        <div className="flex gap-2 justify-center">
          <button
            onClick={resetError}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition"
          >
            Reload Page
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AppWithErrorBoundary() {
  return (
    <SentryErrorBoundary fallback={ErrorFallback} showDialog>
      <App />
    </SentryErrorBoundary>
  );
}
